import { useMemo, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { NumberQuota } from '@ast-data/shared';
import { recordNumberGap, recordNumberHint } from './recordNumberHint';
import RecordNumberHintLine from './RecordNumberHintLine';

/**
 * The help a record-number field gets — Fundzettel-, Befund- and
 * Zeichenblattnummer alike: the highest number and the next under the field
 * (while a new record is being numbered) — or, once the Kontingente are used
 * up, a warning saying so — and a warning at its end where the typed number
 * would leave numbers out. Allowed all the same — the warnings ask for a
 * second look, they refuse nothing (the Kontingent check on save does).
 *
 * Returns the `helperText` and `endAdornment` to hand the text field.
 */
export function useRecordNumberAssist({
    records,
    currentId,
    quotas,
    value,
    offer,
    onApply
}: {
    /** Every record numbered in the same series, the current one included. */
    records: readonly { id: string; number: string }[];
    currentId: string;
    quotas: readonly NumberQuota[];
    /** What the field holds now. */
    value: string;
    /** Show the highest and the button — a new record, editable. */
    offer: boolean;
    onApply: (number: string) => void;
}): { helperText?: ReactNode; endAdornment?: ReactNode } {
    const intl = useIntl();
    const hint = useMemo(
        () => recordNumberHint(records, currentId, quotas),
        [records, currentId, quotas]
    );
    const gap = recordNumberGap(hint, value);
    const gapText = gap
        ? intl.formatMessage(
              { id: 'recordNumber.hint.gap' },
              { count: gap.to - gap.from + 1, from: gap.from, to: gap.to }
          )
        : '';
    return {
        helperText:
            offer && hint ? (
                <RecordNumberHintLine
                    hint={hint}
                    current={value}
                    onApply={onApply}
                />
            ) : undefined,
        endAdornment: gapText ? (
            <InputAdornment position="end">
                <Tooltip title={gapText}>
                    <WarningAmberIcon
                        color="warning"
                        fontSize="small"
                        aria-label={gapText}
                    />
                </Tooltip>
            </InputAdornment>
        ) : undefined
    };
}
